import { t } from 'i18next';
import * as Yup from 'yup';

import RoutePath from 'router/path';

import {
  BridgeDocumentPurpose,
  BridgeHighRiskActivities,
  BridgeRegulatedActivity,
  BusinessAddressType,
  DocumentType,
  IdentifyingInformationType,
  ScreeningResult,
} from './types';

// separate fields will be needed for each step of the form to traverse the form based on validation later
export const STEP_1_FIELDS = {
  KYC_SCREEN: 'kycScreen',
  OFAC_SCREEN: 'ofacScreen',
  VERIFIED_DATABASE_AT: 'verifiedDatabaseAt',
  VERIFIED_PROOF_OF_ADDRESS_AT: 'verifiedProofOfAddressAt',
  NAME: 'name',
  DESCRIPTION: 'description',
  BUSINESS_INDUSTRY: 'businessIndustry',
  EMAIL: 'email',
  BUSINESS_TYPE: 'businessType',
  WEBSITE: 'website',
  REGISTERED_ADDRESS: 'registeredAddress',
  PHYSICAL_ADDRESS: 'physicalAddress',
};

export const STEP_2_FIELDS = {
  IDENTIFYING_INFORMATION: 'identifyingInformation',
  DOCUMENTS: 'documents',
  HAS_MATERIAL_INTERMEDIARY_OWNERSHIP: 'hasMaterialIntermediaryOwnership',
};

export const STEP_3_FIELDS = {
  IS_HIGH_RISK_ACTIVITIES: 'isHighRiskActivities',
  HIGH_RISK_ACTIVITIES: 'highRiskActivities',
  IS_REGULATED_ACTIVITY: 'isRegulatedActivity',
  REGULATED_ACTIVITY: 'regulatedActivity',
  SOURCE_OF_FUNDS: 'sourceOfFunds',
  SOURCE_OF_FUNDS_DESCRIPTION: 'sourceOfFundsDescription',
  ACCOUNT_PURPOSE: 'accountPurpose',
  ACCOUNT_PURPOSE_OTHER: 'accountPurposeOther',
  ESTIMATED_ANNUAL_REVENUE_USD: 'estimatedAnnualRevenueUsd',
  EXPECTED_MONTHLY_PAYMENTS_USD: 'expectedMonthlyPaymentsUsd',
  OPERATES_IN_PROHIBITED_COUNTRIES: 'operatesInProhibitedCountries',
};

export const FORM_FIELDS = {
  ...STEP_1_FIELDS,
  ...STEP_2_FIELDS,
  ...STEP_3_FIELDS,
};

export type FormType = {
  kycScreen: ScreeningResult;
  ofacScreen: ScreeningResult;
  verifiedDatabaseAt?: string;
  verifiedProofOfAddressAt?: string;
  uuid?: string;
  name: string;
  description?: string;
  businessIndustry: string;
  email: string;
  businessType: string;
  website?: string;
  identifyingInformation: IdentifyingInformationType[];
  documents: DocumentType[];
  hasMaterialIntermediaryOwnership: boolean;
  isHighRiskActivities: boolean;
  highRiskActivities: BridgeHighRiskActivities[];
  isRegulatedActivity: boolean;
  regulatedActivity?: BridgeRegulatedActivity;
  sourceOfFunds: string;
  sourceOfFundsDescription?: string;
  accountPurpose: string;
  accountPurposeOther?: string;
  estimatedAnnualRevenueUsd?: string;
  expectedMonthlyPaymentsUsd?: string;
  operatesInProhibitedCountries: boolean;
  registeredAddress: BusinessAddressType;
  physicalAddress: BusinessAddressType;
};

export const FORMIK_INITIAL_VALUES: FormType = {
  kycScreen: { screenedAt: '', result: 'passed' },
  ofacScreen: { screenedAt: '', result: 'passed' },
  verifiedDatabaseAt: undefined,
  verifiedProofOfAddressAt: undefined,
  name: '',
  description: undefined,
  businessIndustry: '',
  email: '',
  businessType: '',
  website: undefined,
  identifyingInformation: [{ type: '', issuingCountry: '', number: '', imageFront: '' }],
  documents: [
    {
      purposes: [],
      file: undefined,
      description: undefined,
    },
  ],
  hasMaterialIntermediaryOwnership: false,
  registeredAddress: {
    streetLine1: undefined,
    streetLine2: undefined,
    city: undefined,
    subdivision: undefined,
    postalCode: undefined,
    country: '',
  },
  physicalAddress: {
    streetLine1: undefined,
    streetLine2: undefined,
    city: undefined,
    subdivision: undefined,
    postalCode: undefined,
    country: '',
  },
  isHighRiskActivities: false,
  highRiskActivities: [BridgeHighRiskActivities.NONE_OF_THE_ABOVE],
  isRegulatedActivity: false,
  regulatedActivity: undefined,
  sourceOfFunds: '',
  sourceOfFundsDescription: undefined,
  accountPurpose: '',
  accountPurposeOther: undefined,
  estimatedAnnualRevenueUsd: undefined,
  expectedMonthlyPaymentsUsd: undefined,
  operatesInProhibitedCountries: false,
};

export const KEYS = {
  ROUTE_MATCH: `${RoutePath.Pwc.Accounts.ById}/:route`,
  ROUTE_MATCH_ID: `${RoutePath.Pwc.Accounts.ById}/:route`,
  DEFAULT_DATE_FORMAT: 'YYYY-MM-DD',
  DEFAULT_DATE_MASK: '9999-99-99',
  STORAGE_KEY: 'merchantKybFormData',
  KYB: 'kyb',
  TITAN: 'titan',
};

export type ModalKey = (typeof KEYS)['KYB' | 'TITAN'];

export const imgAcceptRules = {
  'application/pdf': ['.pdf'],
  'image/jpeg': ['.jpeg', '.jpg'],
  'image/png': ['.png'],
  'image/heic': ['.heic'],
  'image/tiff': ['.tif'],
};

export const RADIO_GROUP_OPTIONS = [
  { key: 'Yes', label: 'Yes', value: true },
  { key: 'No', label: 'No', value: false },
];

export const ESTIMATED_ANNUAL_REVENUE_USD_OPTIONS = [
  { key: '0_99999', label: '$0 - $99,999' },
  { key: '100000_999999', label: '$100,000 - $999,999' },
  { key: '1000000_9999999', label: '$1,000,000 - $9,999,999' },
  { key: '10000000_49999999', label: '$10,000,000 - $49,999,999' },
  { key: '50000000_249999999', label: '$50,000,000 - $249,999,999' },
  { key: '250000000_plus', label: '$250,000,000+' },
];

export const ONBOARDING_LABELS = {
  PANEL_TITLE: ['page.merchantSettings.onboarding.title', 'Onboarding'],
  PANEL_FIELDS: {
    TOS_PAGE: ['page.merchantSettings.onboarding.form.tosPage', 'ToS Page'],
  },
  PANEL_VALUES: {
    ALL_ACCEPTED: ['page.merchantSettings.onboarding.form.allAccepted', 'All Terms of Service have been accepted'],
  },
};

export const LABELS = {
  PANEL_TITLE: ['page.merchantSettings.general.title', 'Bridge KYB'],
  PANEL_DESCRIPTION: [
    'page.merchantSettings.general.description',
    'Bridge KYB Reliance: Enter the date(s) when this merchant’s KYB was approved' +
      ' by our underwriting process.This enables Bridge to rely on our KYB verification' +
      ' and reduces their required documentation for faster merchant onboarding.',
  ],
  PANEL_FIELDS: {
    INFORMATION: ['page.merchantSettings.general.form.information', 'Required Information'],
    BUTTON: ['page.merchantSettings.general.form.button', 'Fill Manually'],
    TOS_PAGE: ['page.merchantSettings.general.form.tosPage', 'Bridge ToS Page'],
  },
  PANEL_VALUES: {
    INFORMATION: [
      'page.merchantSettings.general.formValues.information',
      'Merchant Name, Description, Business Industry, Email, Source of Funds,' +
        ' Business Type, Website, Address, Tax ID Number, Ultimate Beneficial Owner, Ownership Document',
    ],
    BRIDGE_KYB_BUTTON: ['page.merchantSettings.general.formValues.button', 'Bridge KYB'],
    TITAN_AUTO_KYB_BUTTON: ['page.merchantSettings.general.formValues.titanAutoKybButton', ' Start Titan Auto-KYB'],
    TITAN_DATA_AVAILABLE: ['page.merchantSettings.general.formValues.titanDataAvailable', 'Titan Data Available'],
    TITAN_DATA_NOT_AVAILABLE: ['page.merchantSettings.general.formValues.titanDataNotAvailable', 'Titan Data Not Available'],
    AUTO_ONBOARDING_SUCCESS: ['page.merchantSettings.general.formValues.autoKybSuccess', 'Merchant successfully onboarded'],
    AUTO_KYB_FAILED: ['page.merchantSettings.general.formValues.autoKybFailed', 'Auto-KYB: Failed'],
    AUTO_KYB_SUCCESS: ['page.merchantSettings.general.formValues.autoKybSuccess', 'Auto-KYB: Success'],
    AUTO_CREATE_BANK_FAILED: ['page.merchantSettings.general.formValues.autoCreateBankFailed', 'Bank Account: Failed'],
    AUTO_CREATE_BANK_SUCCESS: ['page.merchantSettings.general.formValues.autoCreateBankSuccess', 'Bank Account: Success'],
    DEFAULT_ERROR_MESSAGE: ['page.merchantSettings.general.formValues.defaultErrorMessage', 'An error occurred, please try again later'],
    LOADING: ['page.merchantSettings.general.formValues.loading', 'Loading...'],
    ASSOCIATED_PERSON_BUTTON: ['page.merchantSettings.general.formValues.associatedPersonButton', 'Add UBO'],
  },
  SECTIONS: {
    FIELD_REQUIRED: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.required', 'This field is required'],
    FIELD_OPTIONAL: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.optional', 'Optional'],
    KYB_SCREENING_DATE: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.kybScreeningDate', 'KYB Screening Date'],
    },
    OFAC_SCREENING_DATE: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.ofacScreeningDate', 'OFAC Screening Date'],
    },
    ID_VERIFIED_AT: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.idVerifiedAt', 'ID Verified At (Optional)'],
    },
    PROOF_OF_ADDRESS_VERIFIED_AT: {
      TITLE: [
        'page.merchantSettings.kybMerchantModal.sections.kyb.form.proofOfAddressVerifiedAt',
        'Proof of Address Verified At (Optional)',
      ],
    },
    MERCHANT_NAME: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.merchantName', 'Merchant Name'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.merchantNamePlaceholder', 'Merchant Name'],
    },
    MERCHANT_DESCRIPTION: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.merchantDescription', 'Description'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.merchantDescriptionPlaceholder', 'Merchant Description'],
    },
    BUSINESS_INDUSTRY: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.businessIndustry', 'Business Industry'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.businessIndustryPlaceholder', 'Select Industry'],
    },
    MERCHANT_EMAIL: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.merchantEmail', 'Email'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.merchantEmailPlaceholder', 'Email'],
    },
    BUSINESS_TYPE: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.businessType', 'Business Type'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.businessTypePlaceholder', 'Business Type'],
    },
    WEBSITE: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.website', 'Website'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.websitePlaceholder', 'Website'],
    },
    ADDRESS: {
      REGISTERED_ADDRESS_TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.registeredAddress', 'Registered Address'],
      PHYSICAL_ADDRESS_TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.physicalAddress', 'Physical Address'],
      RESIDENTIAL_ADDRESS_TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.residentialAddress', 'Residential Address'],
      STREET_LINE_1: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.streetLine1Placeholder', 'Street Line 1'],
      STREET_LINE_2: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.streetLine2Placeholder', 'Street Line 2'],
      CITY: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.cityPlaceholder', 'City'],
      COUNTRY: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.countryPlaceholder', 'Country'],
      POSTAL_CODE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.postalCodePlaceholder', 'Postal Code'],
      SUBDIVISION: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.subdivisionPlaceholder', 'State/Region'],
    },
    TAX_ID: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.taxId', 'Tax ID Number'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.taxIdPlaceholder', '1234567890123'],
    },
    FIRST_NAME: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.firstName', 'First Name'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.firstNamePlaceholder', 'Enter First Name'],
    },
    LAST_NAME: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.lastName', 'Last Name'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.lastNamePlaceholder', 'Enter Last Name'],
    },
    EMAIL: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.email', 'Email'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.emailPlaceholder', 'example@mail.com'],
    },
    BIRTH_DATE: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.birthDate', 'Date of Birth'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.birthDatePlaceholder', 'Date'],
    },
    PHONE: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.phone', 'Phone'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.phonePlaceholder', 'Enter Phone Number'],
    },
    TITLE: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.title', 'Title'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.titlePlaceholder', 'Enter Title'],
    },
    OWNERSHIP_PERCENTAGE: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.ownPercentage', 'Ownership Percentage'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.ownPercentagePlaceholder', '%'],
    },
    RELATIONSHIP_AT: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.relationshipAt', 'Relationship Established At'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.relationshipAtPlaceholder', 'Date'],
    },
    HAS_OWNERSHIP: {
      LABEL: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.hasOwnership', 'Has Ownership?'],
    },
    HAS_CONTROL: {
      LABEL: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.hasControl', 'Has Control?'],
    },
    IS_SIGNER: {
      LABEL: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.isSigner', 'Is Signer?'],
    },
    IS_DIRECTOR: {
      LABEL: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.isDirector', 'Is Director? Required if the person resides in EEA.'],
    },
    DESCRIPTION: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.description', 'Description'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.descriptionPlaceholder', 'Description'],
    },
    IDENTIFYING_INFORMATION: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.identifyingInformation', 'Identifying Information'],
      ADD: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.addIdentifyingInformation', 'ADD IDENTIFYING INFORMATION'],
      REMOVE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.removeIdentifyingInformation', 'REMOVE IDENTIFYING INFORMATION'],
      PLACEHOLDER_TYPE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.identifyingInformationPlaceholderType', 'Type'],
      PLACEHOLDER_COUNTRY: [
        'page.merchantSettings.kybMerchantModal.sections.kyb.form.identifyingInformationPlaceholderCountry',
        'Issuing Country',
      ],
      PLACEHOLDER_NUMBER: [
        'page.merchantSettings.kybMerchantModal.sections.kyb.form.identifyingInformationPlaceholderNumber',
        'Identification Number',
      ],
    },
    HAS_MATERIAL_INTERMEDIARY_OWNERSHIP: {
      LABEL: [
        'page.merchantSettings.kybMerchantModal.sections.kyb.form.hasMaterialIntermediaryOwnership',
        'Has Material Intermediary Ownership',
      ],
      TEXT: [
        'page.merchantSettings.kybMerchantModal.sections.kyb.form.hasMaterialIntermediaryOwnershipText',
        'The business has at least one intermediate legal entity owner with 25% or more ownership',
      ],
    },
    DOCUMENTS: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.documents', 'Documents'],
      ADD: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.addDocuments', 'ADD Documents'],
      REMOVE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.removeDocuments', 'REMOVE Documents'],
      LOGO: {
        LABEL: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.documentsLogo', 'Document'],
        UPLOAD: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.documentsUpload', 'Upload Document'],
        SUPPORTED_FORMAT: [
          'page.merchantSettings.kybMerchantModal.sections.kyb.form.documentsSupportedFormat',
          'Supported Formats: JPG, JPEG, PNG, HEIC, TIFF, PDF',
        ],
        MAX_FILE_SIZE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.documentsMaxFileSize', 'Maximum Size: 20MB'],
      },
      PLACEHOLDER_PURPOSE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.documentsPlaceholderPurpose', 'Purpose'],
      PLACEHOLDER_UPLOAD: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.documentsPlaceholderUpload', 'Choose File'],
      PLACEHOLDER_DESCRIPTION: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.documentsPlaceholderDescription', 'Description'],
    },
    HIGH_RISK_ACTIVITIES: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.hiRiskActivities', 'High Risk Activities'],
      ADD: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.hiRiskActivities', 'ADD ACTIVITY'],
      REMOVE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.hiRiskActivities', 'REMOVE ACTIVITY'],
      PLACEHOLDER_ACTIVITY: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.hiRiskActivitiesPlaceholder', 'Hi Risk Activity'],
    },
    REGULATED_ACTIVITY: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.regulatedActivity', 'Regulated Activity'],
      PLACEHOLDER_DESCRIPTION: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.regulatedActivityPlaceholder', 'Description'],
      PLACEHOLDER_AUTHORITY_COUNTRY: [
        'page.merchantSettings.kybMerchantModal.sections.kyb.form.regulatedActivityAuthorityCountry',
        'Authority Country',
      ],
      PLACEHOLDER_AUTHORITY_NAME: [
        'page.merchantSettings.kybMerchantModal.sections.kyb.form.regulatedActivityAuthorityName',
        'Authority Name',
      ],
      PLACEHOLDER_LICENSE_NUMBER: [
        'page.merchantSettings.kybMerchantModal.sections.kyb.form.regulatedActivityLicenseNumber',
        'License Number',
      ],
    },
    SOURCE_OF_FUNDS: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.sourceOfFunds', 'Source of Funds'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.sourceOfFundsPlaceholder', 'Source of Funds'],
    },
    SOURCE_OF_FUNDS_DESCRIPTION: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.sourceOfFundsDescription', 'Source of Funds Description'],
      PLACEHOLDER: [
        'page.merchantSettings.kybMerchantModal.sections.kyb.form.sourceOfFundsDescriptionPlaceholder',
        'Enter the Source of Funds Description',
      ],
    },
    ACCOUNT_PURPOSE: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.accountPurpose', 'Business Primary Purpose'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.accountPurposePlaceholder', 'Other'],
    },
    ACCOUNT_PURPOSE_OTHER: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.accountPurposeOther', 'Other Purpose'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.accountPurposeOtherPlaceholder', 'Enter Other Purpose'],
    },
    ESTIMATED_ANNUAL_REVENUE_USD: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.estimatedAnnualRevenueUsd', 'Estimated Annual Revenue (USD)'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.estimatedAnnualRevenueUsdPlaceholder', '100,000'],
    },
    EXPECTED_MONTHLY_PAYMENTS_USD: {
      TITLE: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.expectedMonthlyPaymentsUsd', 'Estimated Monthly Payment (USD)'],
      PLACEHOLDER: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.expectedMonthlyPaymentsUsdPlaceholder', '10,000'],
    },
    OPERATES_IN_PROHIBITED_COUNTRIES: {
      LABEL: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.operatesInProhibitedCountries', 'Operates in Prohibited Countries'],
      TEXT: [
        'page.merchantSettings.kybMerchantModal.sections.kyb.form.operatesInProhibitedCountriesText',
        'Cuba, Iran, Myanmar, North Korea, and Syria',
      ],
    },
    COPY_BUTTON_LABEL: ['page.merchantSettings.kybMerchantModal.sections.kyb.form.copyButtonLabel', 'Copy'],
  },
  ADORNMENTS: {
    PERCENT: '%',
    HTTPS: 'https://',
  },
  MODAL: {
    TITLE: ['page.merchantSettings.general.modal.title', 'Bridge KYB'],
    CONTINUE: ['page.merchantSettings.general.modal.continue', 'Continue'],
    CONFIRM: ['page.merchantSettings.general.modal.save', 'Save'],
    CANCEL: ['page.merchantSettings.general.modal.cancel', 'Cancel'],
    BACK: ['page.merchantSettings.general.modal.back', 'Back'],
  },
};

const documentsSchema = Yup.array().of(
  Yup.object({
    purposes: Yup.array()
      .of(Yup.string().oneOf(Object.values(BridgeDocumentPurpose)))
      .optional(),

    file: Yup.string().min(1).optional(),

    description: Yup.string().when('purposes', {
      is: (purposes: string[] | undefined) => Array.isArray(purposes) && purposes.includes(BridgeDocumentPurpose.OTHER),
      then: (schema) => schema.required(t('Description is required')).min(1),
      otherwise: (schema) => schema.optional(),
    }),
  }),
);

const identifyingInformationSchema = Yup.array()
  .of(
    Yup.object({
      type: Yup.string().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
      issuingCountry: Yup.string().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
      number: Yup.string().min(1).required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    }),
  )
  .min(1);

const businessAddressSchema = Yup.object().shape({
  streetLine1: Yup.string().min(3).required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
  streetLine2: Yup.string().min(1).optional(),
  city: Yup.string().min(1).optional(),
  subdivision: Yup.string().when('country', {
    is: 'USA',
    then: (schema) => schema.required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    otherwise: (schema) => schema.optional(),
  }),
  postalCode: Yup.string().min(1).optional(),
  country: Yup.string().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
});

const highRiskActivitiesSchema = Yup.array()
  .of(Yup.string().oneOf(Object.values(BridgeHighRiskActivities)))
  .when('isHighRiskActivities', {
    is: true,
    then: (schema) =>
      schema.required('At least one high-risk activity is required').min(1, 'At least one high-risk activity must be selected'),
    otherwise: (schema) => schema.notRequired(),
  });

const regulatedActivitySchema = Yup.object()
  .shape({
    regulatedActivitiesDescription: Yup.string().min(1),
    primaryRegulatoryAuthorityCountry: Yup.string().min(1),
    primaryRegulatoryAuthorityName: Yup.string().min(1),
    licenseNumber: Yup.string().min(1),
  })
  .when('isRegulatedActivity', {
    is: (val: any) => Boolean(val),
    then: (schema) =>
      schema.shape({
        regulatedActivitiesDescription: Yup.string().min(1).required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
        primaryRegulatoryAuthorityCountry: Yup.string().min(1).required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
        primaryRegulatoryAuthorityName: Yup.string().min(1).required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
        licenseNumber: Yup.string().min(1).required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
      }),
    otherwise: () => Yup.object().strip(),
  });

// each element in the array corresponds to a step, 0th = step 1, 1st = step 2, etc.
export const VALIDATION_SCHEMAS = [
  Yup.object().shape({
    [STEP_1_FIELDS.KYC_SCREEN]: Yup.object().shape({
      screenedAt: Yup.string().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    }),
    [STEP_1_FIELDS.OFAC_SCREEN]: Yup.object().shape({
      screenedAt: Yup.string().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    }),
    [STEP_1_FIELDS.VERIFIED_DATABASE_AT]: Yup.string().optional(),
    [STEP_1_FIELDS.VERIFIED_PROOF_OF_ADDRESS_AT]: Yup.string().optional(),
    [STEP_1_FIELDS.NAME]: Yup.string().min(1).max(1024).required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    [STEP_1_FIELDS.DESCRIPTION]: Yup.string().min(1).max(1024).required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    [STEP_1_FIELDS.BUSINESS_INDUSTRY]: Yup.string().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    [STEP_1_FIELDS.BUSINESS_TYPE]: Yup.string().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    [STEP_1_FIELDS.EMAIL]: Yup.string().email().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    [STEP_1_FIELDS.WEBSITE]: Yup.string().min(1).max(1024).url().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    [STEP_1_FIELDS.REGISTERED_ADDRESS]: businessAddressSchema,
    [STEP_1_FIELDS.PHYSICAL_ADDRESS]: businessAddressSchema,
  }),
  Yup.object().shape({
    [STEP_2_FIELDS.HAS_MATERIAL_INTERMEDIARY_OWNERSHIP]: Yup.boolean().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    [STEP_2_FIELDS.IDENTIFYING_INFORMATION]: identifyingInformationSchema,
    [STEP_2_FIELDS.DOCUMENTS]: documentsSchema,
  }),
  Yup.object().shape({
    [STEP_3_FIELDS.IS_HIGH_RISK_ACTIVITIES]: Yup.boolean().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    [STEP_3_FIELDS.HIGH_RISK_ACTIVITIES]: highRiskActivitiesSchema,
    [STEP_3_FIELDS.IS_REGULATED_ACTIVITY]: Yup.boolean().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    [STEP_3_FIELDS.REGULATED_ACTIVITY]: regulatedActivitySchema,
    [STEP_3_FIELDS.SOURCE_OF_FUNDS]: Yup.string().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    [STEP_3_FIELDS.SOURCE_OF_FUNDS_DESCRIPTION]: Yup.string().optional(),
    [STEP_3_FIELDS.ACCOUNT_PURPOSE]: Yup.string().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
    [STEP_3_FIELDS.ACCOUNT_PURPOSE_OTHER]: Yup.string().when([STEP_3_FIELDS.ACCOUNT_PURPOSE], {
      is: (val: any) => String(val) === 'other',
      then: (schema) => schema.required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
      otherwise: (schema) => schema.optional(),
    }),
    [STEP_3_FIELDS.ESTIMATED_ANNUAL_REVENUE_USD]: Yup.string().optional(),
    [STEP_3_FIELDS.EXPECTED_MONTHLY_PAYMENTS_USD]: Yup.string().optional(),
    [STEP_3_FIELDS.OPERATES_IN_PROHIBITED_COUNTRIES]: Yup.boolean().required(t(LABELS.SECTIONS.FIELD_REQUIRED)),
  }),
];
